import React, { useState, useEffect } from 'react'
import { useLocation } from 'react-router'
import Logo from 'features/navigation/components/Logo'
import GlobalJumpTo from 'features/navigation/GlobalJumpTo'
import { PATHS, PRIVATE_PATHS, redirect } from 'utils/paths'
import { useTranslation } from 'react-i18next'
import AvatarMenu from 'features/navigation/components/AvatarMenu'
import SearchInput from 'features/SearchInput/SearchInput'
import CartIcon from 'features/navigation/components/CartIcon'
import { getCurrentUserRole, selectHasAnyTaskManagementBp, selectPersona } from 'features/auth/ducks/selectors'
import { useSelector } from 'react-redux'
import Notifications from 'features/notifications/Notifications'
import styled from 'styled-components'
import { Link } from 'react-router-dom'

import SearchIcon from '@mui/icons-material/Search'
import CloseIcon from '@mui/icons-material/Close'
import MenuIcon from '@mui/icons-material/Menu'
import Tooltip from '@mui/material/Tooltip'
import TravelExploreRoundedIcon from '@mui/icons-material/TravelExploreRounded'
import TipsAndUpdatesOutlinedIcon from '@mui/icons-material/TipsAndUpdatesOutlined'
import { ReactComponent as CalendarIcon } from 'assets/images/calendar.svg'
import GuidanceDrawer from 'features/guidance/GuidanceDrawer'
import useNextSteps, { countActionable } from 'features/guidance/useNextSteps'
import HelpButton from 'features/help/HelpButton'

const TopBar = ({ isloggedin = true, onOpenMobileMenu }) => {
  const { t } = useTranslation()
  const location = useLocation()
  const userRole = useSelector(getCurrentUserRole)
  const hasAnyTaskManagementBp = useSelector(selectHasAnyTaskManagementBp)
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false)
  const [jumpOpen, setJumpOpen] = useState(false)
  const [guidanceOpen, setGuidanceOpen] = useState(false)
  const persona = useSelector(selectPersona) || 'manager'
  const { data: nextStepsData } = useNextSteps(persona, isloggedin)
  const guidanceCount = countActionable(nextStepsData)

  useEffect(() => {
    if (!isloggedin) return undefined
    const onKey = e => {
      if (e.key !== 'j' && e.key !== 'J') return
      if (e.metaKey || e.ctrlKey || e.altKey) return
      const el = e.target
      const tag = el?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el?.isContentEditable) return
      if (window.location.pathname.startsWith(PRIVATE_PATHS.dashboard)) return
      e.preventDefault()
      setJumpOpen(true)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [isloggedin])

  const handleCreateNewBp = () => {
    if (window.location.pathname.includes('create-bp')) {
      window.location.reload()
    } else {
      redirect(PRIVATE_PATHS.createBp)
    }
  }

  const isFrsUser = userRole === 'emergency_service'
  const isResponsiblePerson = userRole === 'responsible_person'
  const isBpListPage = location.pathname === PRIVATE_PATHS.bpList

  return (
    <TopBarWrapper data-app-topbar>
      <DesktopContainer>
        <LeftSection>
          <LogoWrapperDesktop>
            <Logo isloggedin={isloggedin} />
          </LogoWrapperDesktop>

          {isloggedin && (
            <SearchWrapper>
              <SearchInput />
            </SearchWrapper>
          )}
        </LeftSection>

        <RightSection>
          {!isloggedin && (
            <>
              <NavItem to={PATHS.login}>{t('navigation.login')}</NavItem>
              <NavItem to={PATHS.signup}>{t('navigation.signUp')}</NavItem>
            </>
          )}

          {isloggedin && (
            <>
              <HelpButton />
              <JumpButton
                type="button"
                data-tour-id='topbar.jump-to'
                onClick={() => setJumpOpen(true)}
                title="Jump to a building, its compliance tracker, tasks or files (press J)"
              >
                <TravelExploreRoundedIcon style={{ fontSize: 18 }} />
                <span>Jump to…</span>
                <Kbd aria-hidden="true">J</Kbd>
              </JumpButton>

              {!isFrsUser && !isResponsiblePerson && (
                <>
                  {isBpListPage && (
                    <CreateBpButton onClick={handleCreateNewBp}>
                      +&nbsp;{t('navigation.createPassport')}
                    </CreateBpButton>
                  )}
                  <Tooltip title='Basket'>
                    <DesktopIconButton type='button' onClick={() => redirect(PRIVATE_PATHS.cart)} aria-label='Basket'>
                      <CartIcon passive />
                    </DesktopIconButton>
                  </Tooltip>
                </>
              )}
              {!isFrsUser && hasAnyTaskManagementBp && (
                <Tooltip title='Calendar'>
                  <DesktopIconButton type='button' onClick={() => redirect(PRIVATE_PATHS.calendar)} aria-label='Calendar'>
                    <CalendarIcon aria-hidden='true' />
                  </DesktopIconButton>
                </Tooltip>
              )}
              {!isFrsUser && (
                <Tooltip title='Your next steps'>
                  <DesktopIconButton type='button' onClick={() => setGuidanceOpen(true)} aria-label={guidanceCount > 0 ? `Your next steps, ${guidanceCount} to do` : 'Your next steps'}>
                    <BadgeWrap>
                      <StepsIcon style={{ fontSize: 24 }} aria-hidden='true' />
                      {guidanceCount > 0 && <NextStepsBadge aria-hidden='true'>{guidanceCount > 9 ? '9+' : guidanceCount}</NextStepsBadge>}
                    </BadgeWrap>
                  </DesktopIconButton>
                </Tooltip>
              )}
              {!isFrsUser && <IconWrapper><Notifications /></IconWrapper>}
              <AvatarMenu />
            </>
          )}
        </RightSection>
      </DesktopContainer>

      {isloggedin && (
        <MobileContainer>
          {mobileSearchOpen ? (
            <MobileSearchActive>
              <SearchWrapper>
                <SearchInput autoFocus showBoxClose={false} showClear={false} />
              </SearchWrapper>
              <CloseButton onClick={() => setMobileSearchOpen(false)}>
                <CloseIcon />
              </CloseButton>
            </MobileSearchActive>
          ) : (
            <MobileNormalState>
              <LogoWrapper>
                <Logo isloggedin={isloggedin} small />
              </LogoWrapper>

              <MobileActionsRight>
                <HelpButton compact />

                <Tooltip title='Jump to'>
                  <IconAction type='button' data-tour-id='topbar.jump-to' onClick={() => setJumpOpen(true)} aria-label='Jump to'>
                    <TravelExploreRoundedIcon aria-hidden='true' />
                  </IconAction>
                </Tooltip>

                <Tooltip title='Search'>
                  <IconAction type='button' onClick={() => setMobileSearchOpen(true)} $isSearch aria-label='Search'>
                    <SearchIcon aria-hidden='true' />
                  </IconAction>
                </Tooltip>

                {!isFrsUser && !isResponsiblePerson && (
                  <Tooltip title='Basket'>
                    <IconAction type='button' $hideNarrow onClick={() => redirect(PRIVATE_PATHS.cart)} aria-label='Basket'>
                      <CartIcon isMobile={true} passive />
                    </IconAction>
                  </Tooltip>
                )}

                {!isFrsUser && hasAnyTaskManagementBp && (
                  <Tooltip title='Calendar'>
                    <IconAction type='button' $hideNarrow onClick={() => redirect(PRIVATE_PATHS.calendar)} aria-label='Calendar'>
                      <CalendarIcon aria-hidden='true' />
                    </IconAction>
                  </Tooltip>
                )}

                {!isFrsUser && (
                  <Tooltip title='Your next steps'>
                    <IconAction type='button' onClick={() => setGuidanceOpen(true)} aria-label={guidanceCount > 0 ? `Your next steps, ${guidanceCount} to do` : 'Your next steps'}>
                      <BadgeWrap>
                        <StepsIcon aria-hidden='true' />
                        {guidanceCount > 0 && <NextStepsBadge aria-hidden='true'>{guidanceCount > 9 ? '9+' : guidanceCount}</NextStepsBadge>}
                      </BadgeWrap>
                    </IconAction>
                  </Tooltip>
                )}

                {!isFrsUser && (
                  <IconSlot>
                    <Notifications />
                  </IconSlot>
                )}

                <Tooltip title='Open menu'>
                  <IconAction type='button' onClick={onOpenMobileMenu} style={{ marginLeft: 4 }} aria-label='Open menu'>
                    <MenuIcon aria-hidden='true' />
                  </IconAction>
                </Tooltip>

              </MobileActionsRight>
            </MobileNormalState>
          )}
        </MobileContainer>
      )}

      {isloggedin && (
        <>
          <GlobalJumpTo open={jumpOpen} onClose={() => setJumpOpen(false)} />
          <GuidanceDrawer open={guidanceOpen} onClose={() => setGuidanceOpen(false)} mode={persona} />
        </>
      )}
    </TopBarWrapper>
  )
}

export default TopBar

const TopBarWrapper = styled.div`
  width: 100%;
  box-sizing: border-box;
  background-color: ${({ theme }) => theme.colors.navigation};
  position: sticky;
  top: 0;
  z-index: 100;
  box-shadow: 0 2px 10px rgba(0,0,0,0.03);

  @media (max-width: 768px) {
    position: fixed;
    left: 0;
    right: 0;
    top: 0;
    z-index: 1300;
    transform: translateZ(0);
  }
`

const DesktopContainer = styled.div`
  box-sizing: border-box;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: rgb(250, 251, 252);
  height: 88px;
  border-bottom: 2px solid rgb(242, 242, 242);

  @media (max-width: 768px) {
    display: none;
  }
`

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 30px;
  flex: 1;
  min-width: 0;

  @media (max-width: 1100px) {
    gap: 16px;
  }
`

const LogoWrapperDesktop = styled.div`
  flex-shrink: 0;
`

const SearchWrapper = styled.div`
  flex: 1;
  max-width: 600px;
  min-width: 0;
`

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  flex-shrink: 0;
  padding-right: 48px;

  @media (max-width: 1100px) {
    gap: 8px;
    padding-right: 16px;
  }
`

const NavItem = styled(Link)`
  padding: 8px 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
  text-decoration: none;
  &:hover { color: ${({ theme }) => theme.colors.primary}; }
`

const CreateBpButton = styled.button`
  background-color: ${({ theme }) => theme.colors.haiti};
  color: #fff;
  border: 2px solid ${({ theme }) => theme.colors.secondary};
  border-radius: 20px;
  padding: 8px 24px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s;

  &:hover {
    background-color: ${({ theme }) => theme.colors.secondary};
  }
`

const JumpButton = styled.button`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 8px 0 14px;
  border: 1px solid #e6e9ef;
  border-radius: 999px;
  background: #f5f7fa;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#4b5563'};
  font: inherit;
  font-weight: 600;
  font-size: 13.5px;
  cursor: pointer;
  white-space: nowrap;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;

  svg {
    color: ${({ theme }) => theme.colors.secondary || '#33becc'};
  }

  &:hover {
    background: #eef2f7;
    border-color: ${({ theme }) => theme.colors.secondary || '#33becc'};
    color: ${({ theme }) => theme.colors.haiti};
  }
  &:focus-visible {
    outline: 2px solid #33becc;
    outline-offset: 2px;
    border-color: ${({ theme }) => theme.colors.secondary || '#33becc'};
  }

  @media (max-width: 1024px) {
    span { display: none; }
    padding: 0 12px;
    gap: 0;
  }
`

const Kbd = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border: 1px solid #dfe3ea;
  border-bottom-width: 2px;
  border-radius: 6px;
  background: #fff;
  font-size: 11px;
  font-weight: 800;
  line-height: 1;
  color: ${({ theme }) => theme.colors.grey?.[700] || '#6b7280'};

  @media (max-width: 1024px) { display: none; }
`

const IconWrapper = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 8px;

  svg g { fill: ${({ theme }) => theme.colors.haiti}; }
`

const DesktopIconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  border: 0;
  border-radius: 8px;
  overflow: visible;
  background: none;
  cursor: pointer;
  font: inherit;
  color: ${({ theme }) => theme.colors.haiti};

  svg g { fill: ${({ theme }) => theme.colors.haiti}; }

  &:focus-visible {
    outline: 2px solid #33becc;
    outline-offset: 2px;
  }
`

const StepsIcon = styled(TipsAndUpdatesOutlinedIcon)`
  color: ${({ theme }) => theme.colors.haiti};
`
const BadgeWrap = styled.span`
  position: relative; display: inline-flex; align-items: center; justify-content: center; line-height: 0;
`
const NextStepsBadge = styled.span`
  position: absolute;
  top: -10px;
  right: -14px;
  box-sizing: border-box;
  min-width: 20px;
  height: 20px;
  padding: 0 5px;
  border: 2px solid #fff;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.white};
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  z-index: 2;

  @media (max-width: 480px) {
    top: -9px;
    right: -12px;
    min-width: 18px;
    height: 18px;
    padding: 0 4px;
    font-size: 10px;
  }
`

const MobileContainer = styled.div`
  display: none;
  height: 60px;
  background-color: ${({ theme }) => theme.colors.navigation};
  padding: 0 16px;
  box-sizing: border-box;

  @media (max-width: 420px) { padding: 0 10px; }

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
`

const MobileNormalState = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  min-width: 0;
`

const LogoWrapper = styled.div`
  flex-shrink: 0;
  img { height: 28px; width: auto; }

  @media (max-width: 420px) {
    img { height: 24px; }
  }
`

/*
 * Seven tap targets share the bar with the logo, so they tighten in steps as
 * the viewport narrows rather than spilling over the edge.
 */
const MobileActionsRight = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex: 1 1 auto;
  min-width: 0;
  gap: 8px;

  @media (max-width: 480px) { gap: 4px; }
  @media (max-width: 420px) { gap: 2px; }
`

const iconBox = `
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  padding: 0;
  border: 0;
  border-radius: 8px;
  background: none;

  svg {
    width: 22px;
    height: 22px;
    fill: currentColor;
  }
`

const IconAction = styled.button`
  ${iconBox}
  cursor: pointer;
  color: ${({ theme }) => theme.colors.haiti};

  &:focus-visible {
    outline: 2px solid #33becc;
    outline-offset: 2px;
  }

  @media (max-width: 419px) {
    ${({ $hideNarrow }) => ($hideNarrow ? 'display: none;' : '')}
  }
`

const IconSlot = styled.div`
  ${iconBox}
  color: ${({ theme }) => theme.colors.haiti};

  @media (max-width: 419px) {
    ${({ $hideNarrow }) => ($hideNarrow ? 'display: none;' : '')}
  }
`

const MobileSearchActive = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  gap: 10px;
  animation: slideIn 0.2s ease-out;

  @keyframes slideIn {
    from { opacity: 0; transform: translateY(-5px); }
    to { opacity: 1; transform: translateY(0); }
  }
`

const CloseButton = styled.button`
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.grey[600]};
  display: flex;
  align-items: center;
  justify-content: center;
`
